import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingLibraryIcon,
  PencilIcon,
  EyeIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Reference, ReferenceLead, ReferenceStats, ReferenceAccountRow, ReferenceAccountsSummary } from '../types/Reference';
import { referenceApi } from '../services/referenceApi';
import ReferenceForm from './ReferenceForm';
import Layout from './Layout';

type TabValue = 'overview' | 'accounts';

const ReferenceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabValue) || 'overview';

  const [reference, setReference] = useState<Reference | null>(null);
  const [leads, setLeads] = useState<ReferenceLead[]>([]);
  const [stats, setStats] = useState<ReferenceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [imageUrls, setImageUrls] = useState<{
    personal?: string;
    bank?: string;
  }>({});

  // Accounts tab state
  const [accounts, setAccounts] = useState<ReferenceAccountRow[]>([]);
  const [accountsSummary, setAccountsSummary] = useState<ReferenceAccountsSummary | null>(null);
  const [accountsPagination, setAccountsPagination] = useState<{
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsStatusFilter, setAccountsStatusFilter] = useState<'unpaid' | 'paid' | 'rejected' | ''>('');
  const [rejectConfirmRow, setRejectConfirmRow] = useState<ReferenceAccountRow | null>(null);
  const [payModalRow, setPayModalRow] = useState<ReferenceAccountRow | null>(null);
  const [payModalFee, setPayModalFee] = useState(0);
  const [payModalDate, setPayModalDate] = useState('');
  const [payModalError, setPayModalError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [editModalRow, setEditModalRow] = useState<ReferenceAccountRow | null>(null);
  const [editModalFee, setEditModalFee] = useState(0);
  const [editModalStatus, setEditModalStatus] = useState<'unpaid' | 'paid' | 'rejected'>('unpaid');
  const [editModalDate, setEditModalDate] = useState('');
  const [editModalError, setEditModalError] = useState<string | null>(null);
  const [savingActionLeadId, setSavingActionLeadId] = useState<number | null>(null);

  const fetchImageUrls = useCallback(async (referenceId: number, reference: Reference) => {
    try {
      const [personalResponse, bankResponse] = await Promise.all([
        reference.imageUrl ? referenceApi.getImageUrl(referenceId, 'personal') : Promise.resolve({ success: false }),
        reference.bankDocumentUrl ? referenceApi.getImageUrl(referenceId, 'bank') : Promise.resolve({ success: false })
      ]);

      const newImageUrls: { personal?: string; bank?: string } = {};
      
      if (personalResponse.success && 'data' in personalResponse) {
        newImageUrls.personal = (personalResponse.data as { signedUrl: string }).signedUrl;
      }
      
      if (bankResponse.success && 'data' in bankResponse) {
        newImageUrls.bank = (bankResponse.data as { signedUrl: string }).signedUrl;
      }
      
      setImageUrls(newImageUrls);
    } catch (error) {
      console.error('Error fetching image URLs:', error);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [referenceResponse, leadsResponse, statsResponse] = await Promise.all([
        referenceApi.getReferenceById(parseInt(id)),
        referenceApi.getReferenceLeads(parseInt(id)),
        referenceApi.getReferenceStats(parseInt(id))
      ]);

      if (referenceResponse.success) {
        setReference(referenceResponse.data);
        // Fetch image URLs after setting reference
        if (referenceResponse.data.imageUrl || referenceResponse.data.bankDocumentUrl) {
          await fetchImageUrls(parseInt(id), referenceResponse.data);
        }
      } else {
        setError('Failed to fetch reference details');
        return;
      }

      if (leadsResponse.success) {
        setLeads(leadsResponse.data);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch reference data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchReferenceData();
    }
  }, [id, fetchReferenceData]);

  const fetchAccounts = useCallback(async () => {
    if (!id) return;
    setAccountsLoading(true);
    setAccountsError(null);
    try {
      const res = await referenceApi.getReferenceAccounts(parseInt(id), {
        page: accountsPage,
        limit: 20,
        status: accountsStatusFilter || undefined,
        sortBy: 'payment_status',
        sortOrder: 'ASC',
      });
      if (res.success) {
        setAccounts(res.data);
        setAccountsSummary(res.summary);
        setAccountsPagination(res.pagination);
      }
    } catch (err: any) {
      setAccountsError(err.message || 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, [id, accountsPage, accountsStatusFilter]);

  useEffect(() => {
    if (tab === 'accounts' && id) {
      fetchAccounts();
    }
  }, [tab, id, fetchAccounts]);

  const setTab = (newTab: TabValue) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', newTab);
      return next;
    });
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleFormClose = () => {
    setShowEditForm(false);
  };

  const handleFormSave = (updatedReference: Reference) => {
    setReference(updatedReference);
    setShowEditForm(false);
  };

  const handleViewLead = (leadId: number) => {
    try {
      sessionStorage.setItem('leadshive_last_page', window.location.pathname + window.location.search);
    } catch (_) {}
    navigate(`/leads/${leadId}`);
  };

  const openRejectConfirm = (row: ReferenceAccountRow) => {
    setRejectConfirmRow(row);
    setRejectError(null);
  };
  const closeRejectConfirm = () => {
    setRejectConfirmRow(null);
    setRejectError(null);
  };

  const confirmReject = async () => {
    if (!id || !rejectConfirmRow) return;
    setSavingActionLeadId(rejectConfirmRow.leadId);
    setRejectError(null);
    try {
      await referenceApi.updateReferenceLeadAccount(parseInt(id), rejectConfirmRow.leadId, {
        paymentStatus: 'rejected',
        referralFee: 0,
      });
      closeRejectConfirm();
      await fetchAccounts();
    } catch (err: any) {
      setRejectError(err.response?.data?.message || err.message || 'Failed to reject');
    } finally {
      setSavingActionLeadId(null);
    }
  };

  const openPayModal = (row: ReferenceAccountRow) => {
    const today = new Date().toISOString().slice(0, 10);
    setPayModalRow(row);
    setPayModalFee(row.referralFee);
    setPayModalDate(today);
    setPayModalError(null);
  };
  const closePayModal = () => {
    setPayModalRow(null);
    setPayModalError(null);
  };

  const savePayModal = async () => {
    if (!id || !payModalRow) return;
    if (payModalFee < 0 || isNaN(payModalFee)) {
      setPayModalError('Referral fee must be 0 or greater');
      return;
    }
    if (!payModalDate.trim()) {
      setPayModalError('Payment date is required');
      return;
    }
    setSavingActionLeadId(payModalRow.leadId);
    setPayModalError(null);
    try {
      await referenceApi.updateReferenceLeadAccount(parseInt(id), payModalRow.leadId, {
        paymentStatus: 'paid',
        referralFee: payModalFee,
        paymentDate: payModalDate,
      });
      closePayModal();
      await fetchAccounts();
    } catch (err: any) {
      setPayModalError(err.response?.data?.message || err.message || 'Failed to save payment');
    } finally {
      setSavingActionLeadId(null);
    }
  };

  const openEditModal = (row: ReferenceAccountRow) => {
    const today = new Date().toISOString().slice(0, 10);
    setEditModalRow(row);
    setEditModalFee(row.referralFee);
    setEditModalStatus(row.paymentStatus);
    setEditModalDate(row.paymentDate || today);
    setEditModalError(null);
  };
  const closeEditModal = () => {
    setEditModalRow(null);
    setEditModalError(null);
  };

  const saveEditModal = async () => {
    if (!id || !editModalRow) return;
    if (editModalFee < 0 || isNaN(editModalFee)) {
      setEditModalError('Referral fee must be 0 or greater');
      return;
    }
    if (editModalStatus === 'paid' && !editModalDate.trim()) {
      setEditModalError('Payment date is required when status is Paid');
      return;
    }
    setSavingActionLeadId(editModalRow.leadId);
    setEditModalError(null);
    try {
      await referenceApi.updateReferenceLeadAccount(parseInt(id), editModalRow.leadId, {
        referralFee: editModalFee,
        paymentStatus: editModalStatus,
        paymentDate: editModalStatus === 'paid' ? editModalDate : null,
      });
      closeEditModal();
      await fetchAccounts();
    } catch (err: any) {
      setEditModalError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSavingActionLeadId(null);
    }
  };

  const formatNumber = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getStageColor = (stage: string | undefined) => {
    if (!stage) {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (stage.toLowerCase()) {
      case 'captured':
        return 'bg-blue-100 text-blue-800';
      case 'engaged':
        return 'bg-yellow-100 text-yellow-800';
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'win':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error}</p>
        <button 
          onClick={() => navigate('/references')}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to References
        </button>
      </div>
    );
  }

  if (!reference) {
    return (
      <div className="text-center py-12 text-gray-600">
        <p>Reference not found.</p>
        <button 
          onClick={() => navigate('/references')}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to References
        </button>
      </div>
    );
  }

  return (
    <Layout title="Reference Details" subtitle={reference?.name || 'Loading...'}>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/references')} 
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to References
          </button>
        
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {imageUrls.personal ? (
                  <img
                    src={imageUrls.personal}
                    alt={reference.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                    <UserIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-900">{reference.name}</h1>
                <div className="flex items-center space-x-6 text-sm text-gray-600 mt-2">
                  <div className="flex items-center">
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    {reference.contactNumber}
                  </div>
                  <div className="flex items-center">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    {reference.email}
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit Reference
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              tab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab('accounts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              tab === 'accounts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BanknotesIcon className="w-4 h-4 mr-2" />
            Accounts
          </button>
        </nav>
      </div>

      {tab === 'accounts' ? (
        /* Accounts tab */
        <div className="space-y-6">
          {accountsError && (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{accountsError}</div>
          )}
          {accountsSummary != null && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-500">Total Paid</p>
                <p className="text-xl font-semibold text-green-600">{formatNumber(accountsSummary.totalPaid)}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-500">Total Unpaid</p>
                <p className="text-xl font-semibold text-amber-600">{formatNumber(accountsSummary.totalUnpaid)}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-500">Total Rejected</p>
                <p className="text-xl font-semibold text-red-600">{formatNumber(accountsSummary.totalRejected)}</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Filter by status:</span>
                <select
                  value={accountsStatusFilter}
                  onChange={(e) => {
                    setAccountsStatusFilter(e.target.value as 'unpaid' | 'paid' | 'rejected' | '');
                    setAccountsPage(1);
                  }}
                  className="rounded border border-gray-300 py-1 px-2 text-sm"
                >
                  <option value="">All</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                </select>
              </label>
            </div>
            {accountsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Referral Fee</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accounts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">
                            No leads assigned to this reference.
                          </td>
                        </tr>
                      ) : (
                        accounts.map((row) => (
                          <tr key={row.leadId} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm">
                              <button
                                type="button"
                                onClick={() => handleViewLead(row.leadId)}
                                className="text-blue-600 hover:underline font-medium"
                              >
                                {row.leadName}
                              </button>
                              {row.leadUid && (
                                <span className="block text-xs text-gray-400">{row.leadUid}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span
                                className="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                                style={row.stageColor ? { backgroundColor: `${row.stageColor}20`, color: row.stageColor } : undefined}
                              >
                                {row.stageName}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">{formatNumber(row.referralFee)}</td>
                            <td className="px-4 py-2 text-sm">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  row.paymentStatus === 'paid'
                                    ? 'bg-green-100 text-green-800'
                                    : row.paymentStatus === 'rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}
                              >
                                {row.paymentStatus}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">{row.paymentDate || '—'}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className="flex items-center justify-end gap-2">
                                {row.paymentStatus === 'unpaid' ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openPayModal(row)}
                                      disabled={savingActionLeadId !== null}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Pay
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openRejectConfirm(row)}
                                      disabled={savingActionLeadId !== null}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Reject
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openEditModal(row)}
                                    disabled={savingActionLeadId !== null}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Edit
                                  </button>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {accountsPagination && accountsPagination.totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Page {accountsPagination.currentPage} of {accountsPagination.totalPages} ({accountsPagination.totalRecords} records)
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAccountsPage((p) => Math.max(1, p - 1))}
                        disabled={!accountsPagination.hasPrevPage}
                        className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                      >
                        <ChevronLeftIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountsPage((p) => p + 1)}
                        disabled={!accountsPagination.hasNextPage}
                        className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                      >
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Reference Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="w-5 h-5 mr-2" />
              Personal Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Name</label>
                <p className="text-sm text-gray-900">{reference.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  {reference.contactNumber}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  {reference.email}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                <p className="text-sm text-gray-900 flex items-start">
                  <MapPinIcon className="w-4 h-4 mr-2 mt-0.5" />
                  {reference.address}
                </p>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BuildingLibraryIcon className="w-5 h-5 mr-2" />
              Bank Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Bank Account Name</label>
                <p className="text-sm text-gray-900">{reference.bankAccountName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Bank Account Number</label>
                <p className="text-sm text-gray-900">{reference.bankAccountNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Branch Name</label>
                <p className="text-sm text-gray-900">{reference.branchName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Routing Number</label>
                <p className="text-sm text-gray-900">{reference.bankAccountRoutingNumber}</p>
              </div>
              {imageUrls.bank && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Bank Document</label>
                  <img
                    src={imageUrls.bank}
                    alt="Bank document"
                    className="w-32 h-32 object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Commission Structure */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Commission Structure
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {reference.commissionStructure.map((commission) => (
                <div key={commission.stageId} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900 capitalize">{commission.stageName}</span>
                  <span className="text-sm font-semibold text-blue-600">{formatNumber(commission.commissionAmount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Statistics and Leads */}
        <div className="space-y-6">
          {/* Statistics */}
          {stats && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Leads</span>
                  <span className="text-sm font-semibold text-gray-900">{stats.total_leads}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Commission</span>
                  <span className="text-sm font-semibold text-green-600">{formatNumber(stats.total_commission_earned)}</span>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Won Leads</span>
                      <span className="font-medium">{stats.won_leads}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lost Leads</span>
                      <span className="font-medium">{stats.lost_leads}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Commission</span>
                      <span className="font-medium">{formatNumber(stats.avg_commission_per_lead)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recent Leads */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Associated Leads</h3>
            {leads.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No leads associated with this reference yet.</p>
            ) : (
              <div className="space-y-3">
                {leads.slice(0, 5).map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{lead.leadName}</p>
                      <p className="text-xs text-gray-500">ID: {lead.leadId} • {lead.contactNumber}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stageName)}`}>
                        {lead.stageName || 'Unknown Stage'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className="text-sm font-semibold text-green-600">{formatNumber(lead.referenceCommission)}</span>
                      <button
                        onClick={() => handleViewLead(lead.leadId)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="View Lead"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {leads.length > 5 && (
                  <p className="text-xs text-gray-500 text-center">And {leads.length - 5} more leads...</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

        {/* Reject confirmation modal */}
        {rejectConfirmRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject referral payment</h3>
              <p className="text-sm text-gray-600 mb-4">
                Reject the referral for <strong>{rejectConfirmRow.leadName}</strong>? The payment status will be set to Rejected and the referral fee will be set to 0.
              </p>
              {rejectError && (
                <p className="text-sm text-red-600 mb-4">{rejectError}</p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeRejectConfirm}
                  className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmReject}
                  disabled={savingActionLeadId === rejectConfirmRow.leadId}
                  className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {savingActionLeadId === rejectConfirmRow.leadId ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pay modal */}
        {payModalRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Mark as paid</h3>
                <button
                  type="button"
                  onClick={closePayModal}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Record payment for <strong>{payModalRow.leadName}</strong>.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral fee</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={payModalFee}
                    onChange={(e) => setPayModalFee(parseFloat(e.target.value) || 0)}
                    className="w-full rounded border border-gray-300 py-2 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment date</label>
                  <input
                    type="date"
                    value={payModalDate}
                    onChange={(e) => setPayModalDate(e.target.value)}
                    className="w-full rounded border border-gray-300 py-2 px-3 text-sm"
                  />
                </div>
              </div>
              {payModalError && (
                <p className="mt-2 text-sm text-red-600">{payModalError}</p>
              )}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={closePayModal}
                  className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={savePayModal}
                  disabled={savingActionLeadId === payModalRow.leadId}
                  className="px-3 py-1.5 rounded bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {savingActionLeadId === payModalRow.leadId ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit account modal (for paid/rejected rows) */}
        {editModalRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit account</h3>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Update referral and payment for <strong>{editModalRow.leadName}</strong>.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral fee</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={editModalFee}
                    onChange={(e) => setEditModalFee(parseFloat(e.target.value) || 0)}
                    className="w-full rounded border border-gray-300 py-2 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment status</label>
                  <select
                    value={editModalStatus}
                    onChange={(e) => setEditModalStatus(e.target.value as 'unpaid' | 'paid' | 'rejected')}
                    className="w-full rounded border border-gray-300 py-2 px-3 text-sm"
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                {editModalStatus === 'paid' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment date</label>
                    <input
                      type="date"
                      value={editModalDate}
                      onChange={(e) => setEditModalDate(e.target.value)}
                      className="w-full rounded border border-gray-300 py-2 px-3 text-sm"
                    />
                  </div>
                )}
              </div>
              {editModalError && (
                <p className="mt-2 text-sm text-red-600">{editModalError}</p>
              )}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEditModal}
                  disabled={savingActionLeadId === editModalRow.leadId}
                  className="px-3 py-1.5 rounded bg-gray-600 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
                >
                  {savingActionLeadId === editModalRow.leadId ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Form Modal */}
        {showEditForm && (
          <ReferenceForm
            reference={reference}
            onClose={handleFormClose}
            onSave={handleFormSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default ReferenceDetail;
